import { Routes, ɵEmptyOutletComponent } from '@angular/router';
import { MaintenanceGuard } from '../account/guards/maintenance.guard';
import { AssetPageComponent } from '../gallery-shared/pages/asset-page/asset-page.component';
import { AssetVersionPageComponent } from '../gallery-shared/pages/asset-version-page/asset-version-page.component';
import { AllGalleryAssetsResolve } from '../gallery-shared/resolves/all-gallery-assets.resolve';
import { AssetPageActivation } from '../gallery/guards/asset-activate.guard';
import { AssetPageDeactivation } from '../gallery/guards/asset-deactivate.guard';
import { AssetVersionDeactivateGuard } from '../gallery/guards/asset-version-deactivate.guard';
import { AssetVersionGuard } from '../gallery/guards/asset-version.guard';
import { OldModeRedirectGuard } from '../gallery/guards/old-mode-redirect.guard';
import { NotFoundGuard } from '../guards/not-found.guard';
import { GalleryAdvancedGuard } from './guards/gallery-advanced.guard';
import { GalleryErrorGuard } from './guards/gallery-error.guard';
import { GalleryIdentifyGuard } from './guards/gallery-identify.guard';
import { GalleryMaintenanceGuard } from './guards/gallery-maintenance.guard';
import { GalleryReviewGuard } from './guards/gallery-review-guard';
import { GalleryUnsubscribeGuard } from './guards/gallery-unsubscribe.guard';
import { TenantProfileMaintenanceGuard } from './guards/tenant-profile-maintenance.guard';
import { TenantProfileGuard } from './guards/tenant-profile.guard';
import { ViewGalleryIdentificationGuard } from './guards/view-gallery-identification.guard';
import { ViewGalleryRedirectGuard } from './guards/view-gallery-redirect.guard';
import { ViewGalleryGuard } from './guards/view-gallery.guard';
import { EmbedGalleryPageComponent } from './pages/embed-gallery-page/embed-gallery-page.component';
import { GalleryIdentifyPageComponent } from './pages/gallery-identify-page/gallery-identify-page.component';
import { GalleryMaintenancePageComponent } from './pages/gallery-maintenance-page/gallery-maintenance-page.component';
import { GalleryNotFoundPageComponent } from './pages/gallery-not-found-page/gallery-not-found-page.component';
import { GalleryPasswordPageComponent } from './pages/gallery-password-page/gallery-password-page.component';
import { SubscribeToAssetComponent } from './pages/subscribe-to-asset-page/subscribe-to-asset-page.component';
import { TenantProfileMaintenancePageComponent } from './pages/tenant-profile-maintenance-page/tenant-profile-maintenance-page.component';
import { TenantProfilePageComponent } from './pages/tenant-profile-page/tenant-profile-page.component';
import { UnsubscribedBasePageComponent } from './pages/unsubscribed-base-page/unsubscribed-base-page.component';
import { UnsubscribedFromAssetComponent } from './pages/unsubscribed-from-asset-page/unsubscribed-from-asset-page.component';
import { UnsubscribedFromGalleryPageComponent } from './pages/unsubscribed-from-gallery-page/unsubscribed-from-gallery-page.component';
import { ViewGalleryAdvancedModePageComponent } from './pages/view-gallery-advanced-mode-page/view-gallery-advanced-mode-page.component';
import { ViewGalleryBasePageComponent } from './pages/view-gallery-base-page/view-gallery-base-page.component';
import { ViewGalleryPageComponent } from './pages/view-gallery-page/view-gallery-page.component';
import { TenantProfileGalleriesResolve } from './resolves/tenant-profile-galleries.resolve';
import { TenantProfileResolve } from './resolves/tenant-profile.resolve';
import { UnsubscribeGalleryResolve } from './resolves/unsubscribe-gallery.resolve';
import { ViewGalleryAccessLevelResolve } from './resolves/view-gallery-access-level.resolve';
import { ViewGalleryAssetVersionResolve } from './resolves/view-gallery-asset-version.resolve';
import { ViewGalleryAssetResolve } from './resolves/view-gallery-asset.resolve';
import { ViewGallerySectionsResolve } from './resolves/view-gallery-sections.resolve';
import { ViewGalleryResolve } from './resolves/view-gallery.resolve';

export const routes: Routes = [
  {
    path: '',
    canActivate: [ViewGalleryRedirectGuard, ViewGalleryIdentificationGuard],
    children: [
      {
        path: '',
        component: TenantProfilePageComponent,
        canActivate: [TenantProfileGuard],
        resolve: { tenant: TenantProfileResolve, galleries: TenantProfileGalleriesResolve },
      },
      {
        path: 'maintenance',
        component: TenantProfileMaintenancePageComponent,
        canActivate: [TenantProfileMaintenanceGuard],
      },
      {
        path: 'access/:galleryPath',
        canActivate: [GalleryErrorGuard],
        resolve: { tenant: TenantProfileResolve },
        component: GalleryPasswordPageComponent,
        children: [
          {
            path: ':action',
            component: GalleryPasswordPageComponent,
          },
          {
            path: 'review/:reviewCode/:assetId',
            component: GalleryPasswordPageComponent,
          },
          {
            path: ':action/:assetId',
            component: GalleryPasswordPageComponent,
          },
        ],
      },
      {
        path: 'identify/:galleryPath',
        canActivate: [GalleryIdentifyGuard],
        resolve: { gallery: ViewGalleryResolve },
        component: GalleryIdentifyPageComponent,
        children: [
          {
            path: ':action',
            component: GalleryIdentifyPageComponent,
          },
          {
            path: ':action/:assetId',
            component: GalleryIdentifyPageComponent,
          },
          {
            path: ':action/:reviewCode/:assetId',
            component: GalleryIdentifyPageComponent,
          },
          {
            path: ':action/:reviewCode/:assetId/version/:versionId',
            component: GalleryIdentifyPageComponent,
          },
        ],
      },
      {
        path: 'not-found/:galleryPath',
        canActivate: [GalleryErrorGuard, NotFoundGuard],
        resolve: { tenant: TenantProfileResolve },
        component: GalleryNotFoundPageComponent,
        children: [
          {
            path: ':action',
            component: GalleryNotFoundPageComponent,
          },
          {
            path: ':action/:assetId',
            component: GalleryNotFoundPageComponent,
          },
          {
            path: ':action/:assetId/version/:versionId',
            component: GalleryNotFoundPageComponent,
          },
        ],
      },
      {
        path: 'error/:galleryPath',
        canActivate: [GalleryErrorGuard, NotFoundGuard],
        resolve: { tenant: TenantProfileResolve },
        component: GalleryNotFoundPageComponent,
        children: [
          {
            path: ':action',
            component: GalleryNotFoundPageComponent,
          },
          {
            path: ':action/:assetId',
            component: GalleryNotFoundPageComponent,
          },
          {
            path: ':action/:assetId/version/:versionId',
            component: GalleryNotFoundPageComponent,
          },
        ],
      },
      {
        path: 'maintenance',
        canActivate: [MaintenanceGuard],
        component: GalleryMaintenancePageComponent,
      },
      {
        path: 'maintenance/:galleryPath',
        canActivate: [GalleryMaintenanceGuard],
        component: GalleryMaintenancePageComponent,
        children: [
          {
            path: ':action',
            component: GalleryMaintenancePageComponent,
          },
          {
            path: ':action/:assetId',
            component: GalleryMaintenancePageComponent,
          },
          {
            path: ':action/:assetId/version/:versionId',
            component: GalleryMaintenancePageComponent,
          },
        ],
      },
      {
        path: ':galleryId/unsubscribe',
        component: UnsubscribedBasePageComponent,
        canActivate: [GalleryUnsubscribeGuard],
        resolve: { gallery: UnsubscribeGalleryResolve },
        children: [
          {
            path: '',
            canActivate: [ViewGalleryGuard],
            component: UnsubscribedFromGalleryPageComponent,
          },
          {
            path: ':assetId',
            component: UnsubscribedFromAssetComponent,
          },
        ],
      },
      {
        path: ':galleryPath',
        component: ViewGalleryBasePageComponent,
        canActivate: [ViewGalleryGuard],
        resolve: { gallery: ViewGalleryResolve, accessLevel: ViewGalleryAccessLevelResolve },
        children: [
          {
            path: 'advanced',
            component: ViewGalleryAdvancedModePageComponent,
            canActivate: [GalleryAdvancedGuard],
            resolve: { assets: AllGalleryAssetsResolve, sections: ViewGallerySectionsResolve },
            children: [
              {
                path: ':assetId',
                component: AssetPageComponent,
                resolve: { data: ViewGalleryAssetResolve },
                canActivate: [AssetPageActivation],
                canDeactivate: [AssetPageDeactivation],
              },
              {
                path: ':assetId/version/:versionId',
                component: AssetVersionPageComponent,
                resolve: { data: ViewGalleryAssetVersionResolve },
                canActivate: [AssetVersionGuard],
                canDeactivate: [AssetVersionDeactivateGuard],
              },
            ],
          },
          {
            path: 'embed',
            component: EmbedGalleryPageComponent,
            resolve: { sections: ViewGallerySectionsResolve },
            children: [
              {
                path: ':assetId',
                component: AssetPageComponent,
                resolve: { data: ViewGalleryAssetResolve },
                canActivate: [AssetPageActivation],
                canDeactivate: [AssetPageDeactivation],
              },
              {
                path: ':assetId/version/:versionId',
                component: AssetVersionPageComponent,
                resolve: { data: ViewGalleryAssetVersionResolve },
                canActivate: [AssetVersionGuard],
                canDeactivate: [AssetVersionDeactivateGuard],
              },
            ],
          },
          {
            path: 'review/:code',
            canActivate: [GalleryReviewGuard],
            children: [
              {
                path: '',
                component: ɵEmptyOutletComponent,
              },
              {
                path: ':assetId',
                component: ɵEmptyOutletComponent,
              },
            ],
          },
          {
            path: 'subscribe/:assetId',
            component: SubscribeToAssetComponent,
          },
          {
            path: 'view',
            canActivate: [OldModeRedirectGuard],
            children: [
              {
                path: '',
                component: ɵEmptyOutletComponent,
              },
              {
                path: ':assetId',
                component: ɵEmptyOutletComponent,
              },
              {
                path: ':assetId/version/:versionId',
                component: ɵEmptyOutletComponent,
              },
            ],
          },
          {
            path: 'feedback',
            canActivate: [OldModeRedirectGuard],
            children: [
              {
                path: '',
                component: ɵEmptyOutletComponent,
              },
              {
                path: ':assetId',
                component: ɵEmptyOutletComponent,
              },
              {
                path: ':assetId/version/:versionId',
                component: ɵEmptyOutletComponent,
              },
            ],
          },
          {
            path: '',
            component: ViewGalleryPageComponent,
            resolve: { sections: ViewGallerySectionsResolve },
            children: [
              {
                path: ':assetId',
                component: AssetPageComponent,
                resolve: { data: ViewGalleryAssetResolve },
                canActivate: [AssetPageActivation],
                canDeactivate: [AssetPageDeactivation],
              },
              {
                path: ':assetId/version/:versionId',
                component: AssetVersionPageComponent,
                resolve: { data: ViewGalleryAssetVersionResolve },
                canActivate: [AssetVersionGuard],
                canDeactivate: [AssetVersionDeactivateGuard],
              },
            ],
          },
        ],
      },
    ],
  },
];
